/**
 * Robots · Daily Tasks — which businesses had work conducted on a given day.
 *
 * Pick a calendar day and the table lists one row per business, summarising the day's
 * activity there; the business name opens a page with that business's individual
 * tasks. Every meaningful column sorts, and the day defaults to today across every
 * status.
 *
 * The business filter is a picker rather than a text box because its options are
 * knowable: only the businesses that ran something that day can be chosen, so there
 * is no such thing as a search that returns nothing.
 *
 * The day, status and business live in the query string rather than in state so the
 * business page can link straight back to the view it was opened from. They are
 * written with `replace` so changing a filter does not fill up the back button.
 *
 * The day boundary is Pacific, matching the rest of the portal, so a task that ran at
 * 10 PM local lands on the day it actually happened rather than the next UTC one.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { SortableTh, StaticTh } from '../SortableTh';
import { useTableSortState } from '../../hooks/useTableSort';
import { sortRows } from '../../utils/tableSort';
import { formatTimePT } from '../../utils/timezone';
import { SessionExpiredError } from '../../api/session';
import {
  businessTasksPath,
  dayLabel,
  fetchDailyTasks,
  instant,
  pacificToday,
  parseStatusFilter,
  statusLabel,
  summarizeByBusiness,
  TASK_STATUSES,
  type BusinessSummary,
  type DailyTasksReport,
} from '../../api/robotDailyTasks';
import './DailyTasks.css';

function businessSortValue(row: BusinessSummary, key: string): unknown {
  switch (key) {
    case 'businessName':
      return row.businessName;
    case 'taskCount':
      return row.taskCount;
    case 'services':
      return row.services.join(', ');
    case 'firstQueuedAt':
      return instant(row.firstQueuedAt);
    case 'lastQueuedAt':
      return instant(row.lastQueuedAt);
    default:
      return null;
  }
}

const DailyTasks: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const date = searchParams.get('date') || pacificToday();
  const status = parseStatusFilter(searchParams.get('status'));
  const businessParam = searchParams.get('business') ?? '';

  const [report, setReport] = useState<DailyTasksReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const sort = useTableSortState('businessName');

  const updateParams = useCallback(
    (changes: Record<string, string | null>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const key of Object.keys(changes)) {
            const value = changes[key];
            if (value === null) next.delete(key);
            else next.set(key, value);
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // A quick change of date or status leaves the earlier request in flight; keeping
  // the controller means the stale response cannot overwrite the newer one.
  const inFlight = useRef<AbortController | null>(null);

  const load = useCallback(async () => {
    if (!date) return;
    inFlight.current?.abort();
    const controller = new AbortController();
    inFlight.current = controller;

    setLoading(true);
    setError('');
    try {
      const next = await fetchDailyTasks(date, status, controller.signal);
      if (!controller.signal.aborted) setReport(next);
    } catch (err) {
      if (controller.signal.aborted || (err instanceof DOMException && err.name === 'AbortError')) return;
      setReport(null);
      setError(
        err instanceof SessionExpiredError
          ? 'Your session expired. Sign in again to load this report.'
          : err instanceof Error
            ? err.message
            : 'Failed to load the daily tasks report.'
      );
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [date, status]);

  useEffect(() => {
    void load();
    return () => inFlight.current?.abort();
  }, [load]);

  const businesses = useMemo(() => summarizeByBusiness(report?.rows ?? []), [report]);

  // Always alphabetical, independent of how the table happens to be sorted, so the
  // list does not reorder under the cursor after a column header is clicked.
  const businessOptions = useMemo(
    () => businesses.slice().sort((a, b) => a.businessName.localeCompare(b.businessName)),
    [businesses]
  );

  // A business is only selected if it actually ran something in the current view;
  // otherwise the select would sit on a value with no matching option.
  const selectedBusiness = useMemo(
    () => (businesses.some((b) => String(b.businessId) === businessParam) ? businessParam : ''),
    [businesses, businessParam]
  );

  // Changing the day or status can strand a business that has nothing in the new
  // view. The filter already ignores it; drop it from the URL so the address bar
  // agrees with the screen.
  useEffect(() => {
    if (report && businessParam && !selectedBusiness) updateParams({ business: null });
  }, [report, businessParam, selectedBusiness, updateParams]);

  const visible = useMemo(() => {
    const matched = selectedBusiness
      ? businesses.filter((b) => String(b.businessId) === selectedBusiness)
      : businesses;
    return sortRows(matched, sort.sortKey, sort.sortDir, businessSortValue);
  }, [businesses, selectedBusiness, sort.sortKey, sort.sortDir]);

  const taskTotal = report?.rows.length ?? 0;

  return (
    <div className="daily-tasks">
      <div className="daily-tasks-toolbar">
        <div className="daily-tasks-field">
          <label className="form-label" htmlFor="daily-tasks-date">Date</label>
          <input
            id="daily-tasks-date"
            type="date"
            className="form-input"
            value={date}
            onChange={(e) => updateParams({ date: e.target.value })}
          />
        </div>

        <div className="daily-tasks-field">
          <label className="form-label" htmlFor="daily-tasks-status">Status</label>
          <select
            id="daily-tasks-status"
            className="form-input"
            value={status}
            onChange={(e) => updateParams({ status: e.target.value })}
          >
            <option value="">All statuses</option>
            <option value="completed">Completed (conducted)</option>
            {TASK_STATUSES.filter((s) => s !== 'completed').map((s) => (
              <option key={s} value={s}>{statusLabel(s)}</option>
            ))}
          </select>
        </div>

        <div className="daily-tasks-field daily-tasks-field--grow">
          <label className="form-label" htmlFor="daily-tasks-business">Business</label>
          <select
            id="daily-tasks-business"
            className="form-input"
            value={selectedBusiness}
            disabled={businessOptions.length === 0}
            onChange={(e) => updateParams({ business: e.target.value || null })}
          >
            <option value="">
              {businessOptions.length === 0
                ? 'No businesses ran a task'
                : `All businesses (${businessOptions.length})`}
            </option>
            {businessOptions.map((b) => (
              <option key={b.businessId} value={String(b.businessId)}>
                {b.businessName} ({b.taskCount})
              </option>
            ))}
          </select>
        </div>

        <button type="button" className="page-toolbar-btn" onClick={() => void load()} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {error ? <p className="create-order-error" role="alert">{error}</p> : null}

      {report && !error ? (
        <div className="daily-tasks-meta">
          <p className="daily-tasks-summary">
            {businesses.length} {businesses.length === 1 ? 'business' : 'businesses'} conducted {taskTotal}{' '}
            {taskTotal === 1 ? 'task' : 'tasks'} on {dayLabel(report.date)}
            {status ? ` · ${statusLabel(status)}` : ' · every status'}
            {selectedBusiness && visible.length !== businesses.length
              ? ` · ${visible.length} shown`
              : ''}
            {taskTotal > 0 ? ' · open a business name for its tasks' : ''}
          </p>
        </div>
      ) : null}

      <div className="daily-tasks-table-wrapper">
        <table className="daily-tasks-table">
          <thead>
            <tr>
              <SortableTh label="Business" columnKey="businessName" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggleSort} />
              <SortableTh label="Tasks" columnKey="taskCount" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggleSort} />
              <SortableTh label="Services Conducted" columnKey="services" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggleSort} />
              <StaticTh label="Outcome" />
              <SortableTh label="First Task" columnKey="firstQueuedAt" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggleSort} />
              <SortableTh label="Last Task" columnKey="lastQueuedAt" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggleSort} />
            </tr>
          </thead>
          <tbody>
            {visible.map((business) => (
              <tr key={business.businessId}>
                <td className="daily-tasks-business">
                  {/* A real link, so it can also be opened in a new tab. */}
                  <Link
                    className="daily-tasks-business-link"
                    to={businessTasksPath(business.businessId, {
                      date,
                      status,
                      business: selectedBusiness,
                    })}
                  >
                    {business.businessName}
                  </Link>
                </td>
                <td className="daily-tasks-count">{business.taskCount}</td>
                <td>{business.services.join(', ')}</td>
                <td>
                  <span className="daily-tasks-pills">
                    {business.statusCounts.map(({ status: s, count }) => (
                      <span key={s} className={`daily-tasks-status daily-tasks-status--${s}`}>
                        {count} {statusLabel(s)}
                      </span>
                    ))}
                  </span>
                </td>
                <td>{formatTimePT(business.firstQueuedAt)}</td>
                <td>{formatTimePT(business.lastQueuedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* A selected business always has a row, so an empty table means the day
          itself is empty rather than the filter being too narrow. */}
      {!loading && !error && visible.length === 0 ? (
        <p className="page-subtitle">
          No tasks recorded on {dayLabel(date)}
          {status ? ` with status ${statusLabel(status)}` : ''}.
        </p>
      ) : null}
    </div>
  );
};

export default DailyTasks;
